import {Injectable} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {forkJoin, Observable, of} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {catchError, filter, map, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {ContactsService} from '../contacts/contacts.service';
import {BaseService} from '../../../../shared/src/lib/services/base.service';
import {PaymentsService} from '../../../../shared/src/lib/services/payments.service';
import {SitesService} from '../../../../shared/src/lib/services/sites.service';
import {ToastService} from '../../../../shared/src/lib/services/toast.service';
import {
  NotificationModalComponent
} from '../../../../shared/src/lib/modals/notification-modal/notification-modal.component';
import {formatDate} from '@angular/common';
import {GenericModalComponent} from '@shared/modals/generic-modal/generic-modal.component';

@Injectable({
  providedIn: 'root',
})
export class StandbyGeneratorsService extends BaseService {
  item: any = {};
  meta: any = {};
  generatorsService: BaseService;
  insignificantSourceService: BaseService;
  permitsService: BaseService;
  annualReportsService: BaseService;
  permitContactsService: BaseService;

  constructor(http: HttpClient, private contactsService: ContactsService,
              private payments: PaymentsService, private modalService: NgbModal, private sitesService: SitesService,
              private toastService: ToastService) {
    super(`${environment.baseUrl}/standbygenerator/attributes`, http);
    this.generatorsService = new BaseService(`${environment.baseUrl}/standbygenerator/generators`, http);
    this.insignificantSourceService = new BaseService(`${environment.baseUrl}/standbygenerator/insignificant_sources`, http);
    this.permitsService = new BaseService(`${environment.baseUrl}/standbygenerator/permits`, http);
    this.annualReportsService = new BaseService(`${environment.baseUrl}/standbygenerator/annual_reports`, http);
    this.permitContactsService = new BaseService(`${environment.baseUrl}/standbygenerator/permits/contacts`, http);
  }

  public loadData(search: any): Observable<any> {
    return this.getList(search).pipe(
      // filter(response => response.results.length > 0),
      switchMap(r => forkJoin({
        item: of(r),
        generators_meta: this.generatorsService.loadMetadata(),
        insignificant_sources_meta: this.insignificantSourceService.loadMetadata(),
        permits_meta: this.permitsService.loadMetadata(),
        annual_reports_meta: this.annualReportsService.loadMetadata()
      })),
      tap(responses => {

        if (responses.item.results[0]) {
          this.item = {...this.item, ...responses.item.results[0]};
        } else {
          this.item = {};
        }

        this.meta = {...this.meta, ...responses.item.meta};
        this.meta.display_fields = [];
        this.meta.type = 'standbygenerator/attributes';
        this.meta.urlPath = 'standbygenerator';
        this.meta.subItems = ['generators', 'insignificant_sources', 'permits', 'annual_reports'];

        this.meta.fields.generators = this.loadGeneratorsMeta(responses.generators_meta);
        this.meta.fields.permits = this.loadPermitMeta(responses.permits_meta);

        this.meta.fields.annual_reports = this.loadAnnualReportsMeta(responses.annual_reports_meta);

        this.meta.fields.insignificant_sources = responses.insignificant_sources_meta;
        this.meta.fields.insignificant_sources.type = 'standbygenerator/insignificant_sources';
        this.meta.fields.insignificant_sources.display_fields = ['name', 'type', 'description'];
        this.meta.fields.insignificant_sources.foreign_key_field = 'attributes';


      }));
  }

  private getGeneratorFields(item) {
    let displayFields = [
      'date_of_manufacture', 'unit_identification', 'description', 'useAP42', 'total_unburned_hydrocarbons',
      'toc_units', 'particulate_matter', 'pm_units', 'sulfur_dioxide', 'so2_units',
      'carbon_monoxide', 'co_units', 'nitrogen_oxide', 'no_units'];

    if (item.fuel_type === 'butane' || item.fuel_type === 'propane') {
      displayFields = displayFields.concat([
        // 'nitrous_oxide', 'nitrous_oxide_units',
        'carbon_dioxide', 'carbon_dioxide_units', 'methane', 'methane_units'
      ]);
    }

    displayFields = displayFields.concat(['fuel_type', 'fuel_consumption',
      'tank_type', 'tank_location', 'internal_tank_size', 'external_tank_size', 'brake_horsepower',
      'brake_kilowatts', 'generator_make', 'generator_model', 'generator_serial_number',
      'engine_make', 'engine_model', 'engine_serial_number', 'engine_hours']);

    return displayFields;
  }

  private select_choice(selected, item) {
    Object.keys(selected).forEach(key => item['contact_' + key] = selected[key]);
  }

  private get_choices(search) {
    return new Promise(resolve => {
      this.permitContactsService.getList({search}).subscribe(response => {
        resolve(response.results);
      });
    });
  }

  /**
   * Get available generators for the current site (attributes)
   * Filters generators by site globalid for permit selection
   */
  private getGeneratorChoices(permit) {
    const search = permit.attributes ? {search: permit.attributes} : {pk_in: permit.join(',')};
    return new Promise(resolve => {
      this.generatorsService.getList<any>(search).subscribe(response => {
        const choices = response.results.map(item => ({
          display_name: `Unit ID ${item.unit_identification}`,
          value: item.globalid
        }));
        resolve(choices);
      });
    });
  }

  public loadAnnualReportsMeta(annual_reports_meta) {
    let meta = annual_reports_meta;
    meta.type = 'standbygenerator/annual_reports';
    meta.foreign_key_field = 'attributes';
    meta.display_fields = [
      'permit', 'reporting_year', 'submission_date', 'total_fee', 'fee_balance', 'acceptance_datetime'
    ];
    meta.action_buttons = [{
      label: 'Print Report',
      icon: 'fa-print',
      func: item => {
        window.open(
          `${environment.baseUrl}/${meta.type}/${item.globalid}/get_application/`,
          '_blank'
        );
      }
    }]
    meta.subItems = ['generators', 'fees', 'payments'];
    meta.generators = {
      display_fields: ['unit_id', 'previous_engine_hours', 'engine_hours'],
      type: 'standbygenerator/generator_annual_reports',
      // fields: {
      //   // generator: {
      //   //   get_choices: (search) => {
      //   //     return new Promise(resolve => {
      //   //       if (search.attributes) {
      //   //         search = search.attributes
      //   //       }
      //   //       this.generatorsService.getList<any>({search}).subscribe(response => {
      //   //         const choices = response.results.map(item => ({
      //   //           display_name: item.unit_identification,
      //   //           value: item.globalid
      //   //         }));
      //   //         resolve(choices);
      //   //       });
      //   //     });
      //   //   }
      //   // }
      // },
      subItems: ['attachments'],
      attachments: {type: 'standbygenerator/generator_report_attachments'}
    };
    // meta.attachments = {type: 'standbygenerator/annual_reports/attachments'};
    meta.payments = this.payments.meta;
    meta.fees = {
      type: 'fees',
      verbose_name: 'Fee',
      verbose_name_plural: 'Fees',
      display_fields: ['description', 'amount', 'comments'],
      foreign_key_field: 'record_id',
      refreshProperties: parentItem => {
        this.annualReportsService.get<any>(parentItem.globalid).subscribe(response => {
          parentItem.fee = response.result.fee;
          parentItem.fee_balance = response.result.fee_balance;
        });
      }
    };
    meta.fields.permit.get_choices = (search) => {
      return new Promise(resolve => {
        if (search.attributes) {
          search = search.attributes
        }
        this.permitsService.getList<any>({search}).subscribe(response => {
          const choices = response.results.map(item => ({
            display_name: item.number,
            value: item.globalid
          }));
          resolve(choices);
        });
      });
    }
    return meta;
  }

  loadPermitMeta(permits_meta) {
    permits_meta.type = 'standbygenerator/permits';
    permits_meta.verbose_name = 'Permit';
    permits_meta.verbose_name_plural = 'Permits';
    permits_meta.foreign_key_field = 'attributes';
    permits_meta.display_fields = [
      'type', 'issue_date', 'number', 'submission_date', 'acceptance_datetime',
      'application_number', 'old_permit_number', 'fee', 'fee_balance', 'generators'
    ];


    permits_meta.subItems = ['contacts', 'review', 'history', 'attachments', 'notes', 'fees', 'payments'];
    permits_meta.contacts = {
      type: 'standbygenerator/permits/contacts',
      display_fields: ['relationship_type', 'contact_display_name', 'contact_contact_type'],
      foreign_key_field: 'permits',
      fields: {
        contact_first_name: {
          get_choices: (s) => this.contactsService.meta.fields.contact_first_name.get_choices(s),
          select_choice: (selected, item) => this.select_choice(selected, item),
          choices: null
        },
        contact_org_name: {
          get_choices: (s) => this.contactsService.meta.fields.contact_org_name.get_choices(s),
          select_choice: (selected, item) => this.select_choice(selected, item),
          choices: null
        }
      }
    };
    permits_meta.payments = this.payments.meta;
    permits_meta.review = {
      fields: permits_meta.fields.review.children,
      verbose_name_plural: 'Routing/Review',
      verbose_name: 'Review',
      display_fields: ['reviewer', 'status', 'reason', 'order', 'last_edited_date']
    };

    permits_meta.history = {
      display_fields: ['status', 'status_datetime', 'details'],
      type: 'permithistory'
    };
    permits_meta.notes = this.loadNotesMeta();
    permits_meta.action_buttons = [
      {
        label: 'Print Permit',
        icon: 'fa-print',
        func: item => {
          window.open(`${environment.baseUrl}/standbygenerator/permits/${item.globalid}/get_permit/`, '_blank');
        }
      },
      {
        label: 'Print Application',
        icon: 'fa-print',
        func: item => {
          window.open(
            `${environment.baseUrl}/standbygenerator/permits/${item.globalid}/get_application/`,
            '_blank'
          );
        }
      },
      {
        label: 'Re-generate Permit',
        icon: 'fa-print',

        func: item => {
          const modalInstance = this.modalService.open(GenericModalComponent, {size: 'lg', backdrop: 'static'})
          modalInstance.componentInstance.title = 'Confirm Permit Re-generation?'
          modalInstance.componentInstance.text = 'Are you sure you want to re-generate this permit?'
          modalInstance.result.then(() => {
            window.open(`${environment.baseUrl}/standbygenerator/permits/${item.globalid}/get_permit/?force_new=true`, '_blank');
          })
        }
      },
    ];
    permits_meta.notify = {
      label: 'Notify',
      tooltip: 'Notify Responsible Party',
      click: item => {
        forkJoin({
          contacts: this.permitContactsService.getList<any>({search: item.globalid}),
          facility: this.sitesService.get<any>(this.item.site),
          help: this.http.get<any>(`/api-v2/applications/help/?application_type=standby&field=permit_notification_email`)
        }).pipe(
          map(r => {
            r.help = r.help.results[0];
            return r;
          }),
          tap(r => {
            const emailAddresses = r.contacts.results.filter(c => c.contact_email_address).map(contact => contact.contact_email_address);
            const modalInstance = this.modalService.open(NotificationModalComponent, {size: 'lg', backdrop: 'static'})
            modalInstance.componentInstance.item = item;
            modalInstance.componentInstance.notificationContent = `Permit #: ${item.number}
                    <br/>Facility: ${r.facility.result.name}
                    <br/><br/>Via Email To: ${emailAddresses.join(', ')}
                    <br/><br/>Dear Applicant, <br/><br/>${r.help.help_text}`;
            modalInstance.componentInstance.notificationSubject = r.help.help_label;
            modalInstance.result.then(contentResponse => {
              // todo: indicate sending somewhere?
              this.permitsService.customPOST('notify', {
                notification_content: contentResponse.content,
                notification_subject: contentResponse.subject
              }).pipe(
                tap(() => this.toastService.success('Email Sent.')),
                catchError(error => of(this.toastService.warning(`Email failed to send: ${error.message}`)))
              )
            })
          })).subscribe();

      }

    };
    permits_meta.attachments = {type: 'standbygenerator/permits_attachments'};
    permits_meta.fees = {
      type: 'fees',
      verbose_name: 'Fee',
      verbose_name_plural: 'Fees',
      display_fields: ['description', 'amount', 'comments'],
      foreign_key_field: 'record_id',
      refreshProperties: parentItem => {
        this.annualReportsService.get<any>(parentItem.globalid).subscribe(response => {
          parentItem.fee = response.result.fee;
          parentItem.fee_balance = response.result.fee_balance;
        });
      }
    };

    // Set up the get_choices for generator selection
    permits_meta.fields.generators.get_choices = (s) => this.getGeneratorChoices(s)


    return permits_meta;
  }

  loadGeneratorsMeta(generators_meta) {
    generators_meta.label_field = 'unit_identification';
    generators_meta.label_color = [255, 0, 0, 1];
    generators_meta.featureService = 'https://gis.epa.guam.gov/arcgis/rest/services/StandbyGeneratorsApplications/FeatureServer/0';
    generators_meta.type = 'standbygenerator/generators';
    // generators_meta.verbose_name = 'Generator';
    // generators_meta.verbose_name_plural = 'Generators';
    generators_meta.foreign_key_field = 'attributes';
    generators_meta.display_fields = [
      'date_of_manufacture', 'service_start_date', 'unit_identification', 'description', 'useAP42', 'total_unburned_hydrocarbons',
      'toc_units', 'particulate_matter', 'pm_units', 'sulfur_dioxide', 'so2_units',
      'carbon_monoxide', 'co_units', 'nitrogen_oxide', 'no_units',
      'carbon_dioxide', 'carbon_dioxide_units', 'methane', 'methane_units', 'benzene', 'benzene_units',
      // 'nitrous_oxide', 'nitrous_oxide_units',
      'fuel_type', 'fuel_consumption', 'fuel_consumption_units', 'tank_type',
      'tank_location', 'internal_tank_size', 'external_tank_size', 'brake_horsepower',
      'brake_kilowatts', 'generator_make', 'generator_model', 'generator_serial_number',
      'engine_make', 'engine_model', 'engine_serial_number', 'engine_hours', 'horizontal_collection_method'
    ];
    generators_meta.subItems = ['attachments'];
    generators_meta.annual_reports = {
      display_fields: ['engine_hours', 'report'],
      type: 'standbygenerator/generator_annual_reports',
      fields: {
        report: {
          get_choices: () => {
            generators_meta.annual_reports.fields.report.label = 'Submission Date';
            return new Promise(resolve => {
              this.annualReportsService.getList<any>({search: this.item.globalid}).subscribe(response => {
                const choices = response.results.map(item => ({
                  display_name: formatDate(item.submission_date, 'short', 'en-US'),
                  value: item.globalid
                }));
                resolve(choices);
              });
            });
          }
        }
      },
      subItems: ['attachments'],
      attachments: {type: 'standbygenerator/generator_report_attachments'}
    };
    generators_meta.attachments = {type: 'standbygenerator/generators_attachments'};

    return generators_meta;
  }

  loadNotesMeta() {
    return {
      type: 'notes',
      verbose_name: 'Note',
      verbose_name_plural: 'Notes',
      foreign_key_field: 'record_id',
      display_fields: ['text', 'created_user', 'created_date', 'last_edited_user', 'last_edited_date']
    };
  }
}
